import { ReloadOutlined } from '@ant-design/icons';
import { ExpressionType } from '@framework/engine/schema/expressions/IExpression.ts';
import { type IReference } from '@framework/engine/schema/expressions/references/IReference.ts';
import { useExpressionValue } from '@framework/engine/schema/expressions/render/hooks/useExpressionValue.tsx';
import { useFormItemEnabled } from '@framework/form/item/useFormItemEnabled.ts';
import { Button, Popconfirm, Tooltip } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { isReferenceObjectValue } from '../createMapSchema.ts';

export interface IMapRestoreDefaultControlProps {
  /** Path del campo mappa (es. `options.name`), per risolvere le dipendenze. */
  fieldName: string;
  /** Reference al tag semantico MAPPA da cui ripristinare il valore originale. */
  reference: IReference;
  /**
   * Applica il valore originale (oggetto del tag `{ root, root@... }`) ai
   * campi della mappa. La distribuzione sui campi/editor è a carico del
   * chiamante (che ha accesso agli editor delle intersezioni).
   */
  onRestore: (value: Record<string, unknown>) => void;
}

/**
 * Controllo (istruttoria BO) che ripristina il widget mappa al valore
 * originale compilato in Front Office, letto dal tag semantico MAPPA indicato
 * da `reference`. A differenza del default (che agisce solo su campo vuoto),
 * è ri-applicabile anche dopo che l'operatore ha modificato la mappa: per
 * questo chiede conferma (sovrascrive le modifiche correnti).
 */
export function MapRestoreDefaultControl(
  props: IMapRestoreDefaultControlProps,
) {
  const { fieldName, reference, onRestore } = props;
  const { t } = useTranslation();
  const { visuallyDisabled } = useFormItemEnabled();

  const expression = useMemo(
    () => ({ type: ExpressionType.Reference as const, value: reference }),
    [reference],
  );

  // Legge il valore originale dal tag semantico (context live): è quello FO,
  // stabile, non il valore eventualmente modificato dall'operatore.
  const result = useExpressionValue(fieldName, expression);
  const value =
    result.type === 'value' && isReferenceObjectValue(result.result)
      ? result.result
      : null;

  // Niente da ripristinare se il tag non ha un valore.
  const disabled = visuallyDisabled || value == null;

  return (
    <Popconfirm
      title={t('map-restore-default.title', 'Ripristina valore originale')}
      description={t(
        'map-restore-default.description',
        'Le modifiche correnti alla mappa verranno sostituite con il valore originale.',
      )}
      okText={t('map-restore-default.ok', 'Ripristina')}
      cancelText={t('map-restore-default.cancel', 'Annulla')}
      onConfirm={() => value && onRestore(value)}
      disabled={disabled}
    >
      <Tooltip
        title={t(
          'map-restore-default.tooltip',
          'Ripristina il valore originale',
        )}
      >
        <Button
          type="default"
          disabled={disabled}
          style={{ borderWidth: '1px' }}
          aria-label={t(
            'map-restore-default.aria',
            'Ripristina valore originale',
          )}
          icon={<ReloadOutlined />}
        />
      </Tooltip>
    </Popconfirm>
  );
}
